import { computed, ref, reactive, watch, type ComputedRef } from 'vue'
import { createGlobalState, useAsyncState } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { useToken } from './useToken'
import type {
  MemberNoticeDTO,
  MessageNoticeDTO,
  UnreadCount,
  MessageType,
  NoticeItem
} from '@/types/notice'
import { MESSAGE_TYPE } from '@/types/notice'
import type { PageRequest, PageResponse, Response } from '@/types/http'
import {
  memberNoticePage,
  memberNoticeRead,
  deleteNotice,
  notifyMessageQuery,
  updateMessageRead,
  deleteMessage
} from '@/api/notice'
import { http } from '@/utils/http'
import dayjs from 'dayjs'
import type { LangCode } from '@/types/config'
import { formatNoticeMultiLang } from '@/utils/noticeFormatMultiLang'
import {
  translatePaymentNoticeContent,
  translatePaymentNoticeOtherContent,
  translatePaymentNoticeTitle
} from '@/utils/paymentNoticeDisplay'

const PAGE_SIZE = 10 // 固定每页数量

/**
 * 全局共享 - 消息中心
 */
export const useNoticeCenter = createGlobalState(() => {
  const { isLoggedIn } = useToken()
  const { t, locale } = useI18n()

  // ===== 状态 =====
  // 系统通知（messageType=1）
  const noticeList = ref<NoticeItem[]>([])
  const noticeRawList = ref<MemberNoticeDTO[]>([])
  const noticePagination = ref({ current: 0, total: 0, pages: 0 })

  // 活动通知（messageType=2）
  const discountList = ref<NoticeItem[]>([])
  const discountRawList = ref<MemberNoticeDTO[]>([])
  const discountPagination = ref({ current: 0, total: 0, pages: 0 })

  // 交易通知（messageType=3）
  const paymentList = ref<NoticeItem[]>([])
  const paymentRawList = ref<MessageNoticeDTO[]>([])
  const paymentPagination = ref({ current: 0, total: 0, pages: 0 })

  // 未读数统一管理（reactive 状态）
  const unreadCounts = reactive<UnreadCount>({
    totalUnreadCount: 0,
    platformNoticeUnreadCount: 0, // 对应 MESSAGE_TYPE.NOTICE
    discountNoticeUnreadCount: 0, // 对应 MESSAGE_TYPE.DISCOUNT
    notifyUnreadCount: 0 // 对应 MESSAGE_TYPE.PAYMENT
  })

  // ===== 获取对应的列表和分页 =====
  const getList = (type: MessageType) => {
    switch (type) {
      case MESSAGE_TYPE.NOTICE:
        return noticeList
      case MESSAGE_TYPE.DISCOUNT:
        return discountList
      case MESSAGE_TYPE.PAYMENT:
        return paymentList
    }
  }

  const getPagination = (type: MessageType) => {
    switch (type) {
      case MESSAGE_TYPE.NOTICE:
        return noticePagination
      case MESSAGE_TYPE.DISCOUNT:
        return discountPagination
      case MESSAGE_TYPE.PAYMENT:
        return paymentPagination
    }
  }

  const getRawList = (type: MessageType) => {
    switch (type) {
      case MESSAGE_TYPE.NOTICE:
        return noticeRawList
      case MESSAGE_TYPE.DISCOUNT:
        return discountRawList
      case MESSAGE_TYPE.PAYMENT:
        return paymentRawList
    }
  }

  // ===== 内部工具函数 =====

  // 更新总未读数（减去指定数量）
  const updateTotalUnread = (decreaseCount: number) => {
    if (decreaseCount > 0) {
      unreadCounts.totalUnreadCount = Math.max(0, unreadCounts.totalUnreadCount - decreaseCount)
    }
  }

  // 更新对应 tab 的未读数（减去指定数量）
  const updateTabUnread = (type: MessageType, decreaseCount: number) => {
    if (decreaseCount > 0) {
      if (type === MESSAGE_TYPE.NOTICE) {
        unreadCounts.platformNoticeUnreadCount = Math.max(
          0,
          unreadCounts.platformNoticeUnreadCount - decreaseCount
        )
      } else if (type === MESSAGE_TYPE.DISCOUNT) {
        unreadCounts.discountNoticeUnreadCount = Math.max(
          0,
          unreadCounts.discountNoticeUnreadCount - decreaseCount
        )
      } else if (type === MESSAGE_TYPE.PAYMENT) {
        unreadCounts.notifyUnreadCount = Math.max(0, unreadCounts.notifyUnreadCount - decreaseCount)
      }
    }
  }

  // 更新所有未读数（从接口返回的数据）
  const updateUnreadCounts = (data: UnreadCount) => {
    unreadCounts.totalUnreadCount = data.totalUnreadCount
    unreadCounts.platformNoticeUnreadCount = data.platformNoticeUnreadCount
    unreadCounts.discountNoticeUnreadCount = data.discountNoticeUnreadCount
    unreadCounts.notifyUnreadCount = data.notifyUnreadCount
  }

  // 从 HTML 中提取文本，将图片替换为【图片】
  const extractTextAndReplaceImages = (html?: string): string => {
    if (!html) return ''
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    const images = tempDiv.getElementsByTagName('img')
    for (let i = 0; i < images.length; i++) {
      const img = images[i]
      const imgStr = t('图片')
      const replacement = document.createTextNode('【' + imgStr + '】')
      img.parentNode?.replaceChild(replacement, img)
    }
    return tempDiv.innerText || ''
  }

  const formatTime = (time?: string | number) => {
    if (!time) return ''
    return dayjs(time).format('YYYY-MM-DD HH:mm:ss')
  }

  const normalizeRecords = (
    records: Array<MemberNoticeDTO | MessageNoticeDTO>,
    type: MessageType
  ): NoticeItem[] => {
    const normalizedRecords =
      type === MESSAGE_TYPE.PAYMENT
        ? records
        : formatNoticeMultiLang(records as MemberNoticeDTO[], locale.value as LangCode)

    return normalizedRecords.map((item) => normalizeNoticeItem(item, type))
  }

  const refreshFormattedList = (type: MessageType) => {
    const list = getList(type)
    const rawList = getRawList(type)

    if (!rawList.value.length) return

    list.value = normalizeRecords(rawList.value, type)
  }

  const markRawMessagesAsRead = (type: MessageType, ids: string[]) => {
    if (type === MESSAGE_TYPE.PAYMENT) {
      paymentRawList.value.forEach((item) => {
        if (ids.includes(String(item.id))) {
          item.readStatus = true
        }
      })
      return
    }

    const rawList = type === MESSAGE_TYPE.NOTICE ? noticeRawList : discountRawList
    rawList.value.forEach((item) => {
      if (ids.includes(String(item.id))) {
        item.readStatus = 1
      }
    })
  }

  const removeRawMessages = (type: MessageType, ids: string[]) => {
    if (type === MESSAGE_TYPE.PAYMENT) {
      paymentRawList.value = paymentRawList.value.filter((item) => !ids.includes(String(item.id)))
      return
    }

    const rawList = type === MESSAGE_TYPE.NOTICE ? noticeRawList : discountRawList
    rawList.value = rawList.value.filter((item) => !ids.includes(String(item.id)))
  }
  // ===== 数据格式化 =====
  // 格式化消息数据：统一 readStatus 为 boolean，处理 title 和 briefContent
  const normalizeNoticeItem = (
    item: MemberNoticeDTO | MessageNoticeDTO,
    type: MessageType
  ): NoticeItem => {
    const baseItem = {
      id: String(item.id), // 转换为 string
      readStatus: false, // 默认值
      // sendTime: item.sendTime,
      // createTime: item.createTime,
      time: formatTime(item.sendTime || item.createTime)
    }

    if (type === MESSAGE_TYPE.PAYMENT) {
      // 支付消息
      const paymentItem = item as MessageNoticeDTO

      // 处理 title：使用 translateTitle 处理后加上【】 好像不用-----待确认
      const translatedTitle = translatePaymentNoticeTitle(paymentItem, t).trim()
      // const title = '【' + translatedTitle + '】'

      // 处理 briefContent：根据 templateType 处理
      let briefContent = ''
      const orderNo = paymentItem.templateParams?.orderNo
      let tips = ''

      if ([1, 2].includes(paymentItem.templateType ?? 0)) {
        tips = translatedTitle + translatePaymentNoticeContent(paymentItem, t)
      } else {
        tips = translatePaymentNoticeOtherContent(paymentItem, t)
      }

      briefContent = t('尊敬的客户') + tips + t('订单编号') + `${orderNo} `

      return {
        ...baseItem,
        title: translatedTitle,
        briefContent,
        readStatus: paymentItem.readStatus ?? false,
        templateContent: paymentItem.templateContent,
        templateType: paymentItem.templateType,
        templateParams: paymentItem.templateParams
      }
    } else {
      // 系统通知/活动通知
      const memberItem = item as MemberNoticeDTO

      // title 直接使用原 title
      const title = memberItem.title

      // briefContent：使用 extractTextAndReplaceImages 处理 content
      const briefContent = extractTextAndReplaceImages(memberItem.content)

      return {
        ...baseItem,
        title,
        content: memberItem.content,
        briefContent,
        messageId: memberItem.messageId,
        readStatus: memberItem.readStatus === 1, // 0/1 转为 boolean
        deleteStatus: memberItem.deleteStatus
      }
    }
  }

  // ===== API =====
  type FetchNoticeListResult = { list: NoticeItem[]; total: number; pages: number }

  // 获取消息列表（分页）
  const fetchNoticeListInternal = async (
    type: MessageType,
    page: number = 1,
    pageSize: number = PAGE_SIZE
  ): Promise<FetchNoticeListResult> => {
    const list = getList(type)
    const pagination = getPagination(type)
    let result: PageResponse<MemberNoticeDTO | MessageNoticeDTO>

    if (type === MESSAGE_TYPE.PAYMENT) {
      // 交易通知
      const params: PageRequest = {
        current: page,
        size: pageSize,
        model: { type: MESSAGE_TYPE.PAYMENT },
        sort: 'createTime',
        order: 'DESC'
      }
      result = await notifyMessageQuery(params)
    } else {
      // 系统通知/活动通知
      const params: PageRequest = {
        current: page,
        size: pageSize,
        model: { messageType: type },
        sort: 'createTime',
        order: 'DESC'
      }
      result = await memberNoticePage(params)
    }

    const records = result.data?.records || []
    const total = result.data?.total ?? 0
    const pages = result.data?.pages ?? 0

    // 更新对应 tab 的未读数（从列表接口返回的 extra?.unreadCount 获取）
    const unreadCount = Number(result.extra?.unreadCount || 0)
    if (type === MESSAGE_TYPE.NOTICE) {
      unreadCounts.platformNoticeUnreadCount = unreadCount
    } else if (type === MESSAGE_TYPE.DISCOUNT) {
      unreadCounts.discountNoticeUnreadCount = unreadCount
    }
    // 这个支付通知的接口没有返回活动通知的未读数
    // else if (type === MESSAGE_TYPE.PAYMENT) {
    //   unreadCounts.notifyUnreadCount = unreadCount
    // }

    const rawList = getRawList(type)
    if (type === MESSAGE_TYPE.PAYMENT) {
      rawList.value = records as MessageNoticeDTO[]
    } else {
      rawList.value = records as MemberNoticeDTO[]
    }

    // 格式化数据
    const formattedList = normalizeRecords(rawList.value, type)
    // console.log('formattedList',formattedList)
    // 只替换当前页数据
    list.value = formattedList
    pagination.value = { current: page, total, pages }

    return {
      list: formattedList,
      total,
      pages
    }
  }

  const { isLoading: isNoticeListLoading, execute: executeFetchNoticeList } =
    useAsyncState<FetchNoticeListResult | null>(fetchNoticeListInternal, null, { immediate: false })

  const fetchNoticeList = (
    type: MessageType,
    page: number = 1,
    pageSize: number = PAGE_SIZE
  ): Promise<FetchNoticeListResult | null> => executeFetchNoticeList(0, type, page, pageSize)

  // 刷新第一页（清空+重新加载）
  const refreshNoticeList = async (type: MessageType) => {
    const list = getList(type)
    const pagination = getPagination(type)

    // 清空数据
    list.value = []
    getRawList(type).value = []
    pagination.value = { current: 0, total: 0, pages: 0 }

    // 加载第一页
    return fetchNoticeList(type, 1, PAGE_SIZE)
  }

  // 跳转到指定页
  const goToPage = async (type: MessageType, page: number) => {
    const pagination = getPagination(type)
    const targetPage = Math.max(1, Math.min(page, pagination.value.pages || 1))
    if (targetPage === pagination.value.current) return
    await fetchNoticeList(type, targetPage, PAGE_SIZE)
  }

  // 检查是否需要加载第一页
  const shouldLoadFirstPage = (type: MessageType): boolean => {
    const list = getList(type)
    const pagination = getPagination(type)
    return list.value.length === 0 || pagination.value.current === 0
  }

  // ===== 获取未读数接口 =====
  async function getUnreadCountTotal() {
    return await http<Response<UnreadCount>>('/app-api/member/notice/unreadCount', {
      method: 'POST'
    })
  }

  const fetchTotal = async () => {
    const res = await getUnreadCountTotal()

    return res?.data ?? null
  }
  const {
    state: unreadStateFromApi,
    isLoading: isUnreadCountLoading,
    error: unreadCountError,
    execute: getTotalUnreadCount
  } = useAsyncState<UnreadCount | null>(fetchTotal, null, { immediate: !!isLoggedIn.value })

  // 监听接口返回的数据，更新 reactive 状态
  watch(
    () => unreadStateFromApi.value,
    (newData) => {
      if (newData) {
        updateUnreadCounts(newData)
      }
    },
    { immediate: true }
  )

  // 登录状态变化时重新获取未读数
  watch(isLoggedIn, (v) => {
    if (v) {
      getTotalUnreadCount()
    }
  })

  // 语言切换时基于当前页原始数据重新生成展示文案
  watch(
    () => locale.value,
    () => {
      refreshFormattedList(MESSAGE_TYPE.NOTICE)
      refreshFormattedList(MESSAGE_TYPE.DISCOUNT)
      refreshFormattedList(MESSAGE_TYPE.PAYMENT)
    }
  )

  // 标记已读
  const markAsRead = async (type: MessageType, ids: string[]) => {
    if (ids.length === 0) return

    if (type === MESSAGE_TYPE.PAYMENT) {
      await updateMessageRead({ paramList: ids })
    } else {
      await memberNoticeRead({ paramList: ids })
    }

    // 更新本地状态
    const list = getList(type)
    let unreadCountBefore = 0
    list.value.forEach((item) => {
      if (ids.includes(item.id)) {
        if (!item.readStatus) {
          unreadCountBefore++
        }
        item.readStatus = true
      }
    })
    markRawMessagesAsRead(type, ids)

    // 更新未读数
    updateTotalUnread(unreadCountBefore)
    updateTabUnread(type, unreadCountBefore)
  }

  // 删除消息
  const deleteMessages = async (type: MessageType, ids: string[]) => {
    // console.log('ids,type',ids,type)
    if (ids.length === 0) return

    if (type === MESSAGE_TYPE.PAYMENT) {
      await deleteMessage({ paramList: ids })
    } else {
      await deleteNotice({ paramList: ids })
    }

    // 从列表中移除
    const list = getList(type)
    const pagination = getPagination(type)

    // 计算删除的未读数量
    const unreadCountBefore = list.value.filter(
      (item) => ids.includes(item.id) && !item.readStatus
    ).length

    // console.log('deleteMessages  ',list.value,)
    list.value = list.value.filter((item) => !ids.includes(item.id))
    removeRawMessages(type, ids)
    // console.log('unreadCountBefore!!! 计算删除的未读数量 ', unreadCountBefore)
    // 更新未读数
    updateTotalUnread(unreadCountBefore)
    updateTabUnread(type, unreadCountBefore)

    // 更新分页总数
    pagination.value.total = Math.max(0, pagination.value.total - ids.length)

    // 如果当前页为空，自动跳转到第一页
    if (list.value.length === 0 && pagination.value.current > 1) {
      await fetchNoticeList(type, 1, PAGE_SIZE)
    } else if (list.value.length === 0 && pagination.value.current === 1) {
      // 第一页为空，重新加载第一页（刷新数据）
      await fetchNoticeList(type, 1, PAGE_SIZE)
    }
  }

  // 获取分页信息（用于 Pagination 组件）
  const getPaginationProps = (type: MessageType) => {
    const pagination = getPagination(type)
    return {
      total: pagination.value.total,
      pageSize: PAGE_SIZE,
      currentPage: pagination.value.current,
      pages: pagination.value.pages
    }
  }

  // ===== 导出 =====
  return {
    // 数据
    noticeList,
    discountList,
    paymentList,

    // 分页状态
    noticePagination: computed(() => ({
      total: noticePagination.value.total,
      pageSize: PAGE_SIZE,
      currentPage: noticePagination.value.current,
      pages: noticePagination.value.pages
    })),
    discountPagination: computed(() => ({
      total: discountPagination.value.total,
      pageSize: PAGE_SIZE,
      currentPage: discountPagination.value.current,
      pages: discountPagination.value.pages
    })),
    paymentPagination: computed(() => ({
      total: paymentPagination.value.total,
      pageSize: PAGE_SIZE,
      currentPage: paymentPagination.value.current,
      pages: paymentPagination.value.pages
    })),

    // 方法
    fetchNoticeList,
    refreshNoticeList,
    goToPage,
    shouldLoadFirstPage,
    markAsRead,
    deleteMessages,
    getPaginationProps,

    // 未读数（统一 reactive 状态）
    totalUnreadCount: computed(() => unreadCounts.totalUnreadCount),
    noticeUnreadCount: computed(() => unreadCounts.platformNoticeUnreadCount),
    discountUnreadCount: computed(() => unreadCounts.discountNoticeUnreadCount),
    paymentUnreadCount: computed(() => unreadCounts.notifyUnreadCount),
    isNoticeListLoading,
    unreadCountLoading: isUnreadCountLoading,
    unreadCountError,
    getTotalUnreadCount
  }
})

export type UseNoticeCenterReturn = ReturnType<typeof useNoticeCenter>
